import { useCallback, useMemo } from 'react';

import { useAuthContext } from '@fhs/auth/src/hooks';
import {
  CartPaymentCardType,
  ICommitDeliveryInput,
} from '@fhs-legacy/frontend/src/generated/graphql-gateway';
import { useCartContext } from '@fhs-legacy/frontend/src/state/cart';
import { useFlag } from '@fhs-legacy/frontend/src/state/launchdarkly';
import { useOrderContext } from '@fhs-legacy/frontend/src/state/order';
import { useStoreContext } from '@fhs-legacy/frontend/src/state/store';
import { LaunchDarklyFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';

import { BasePipelineContext, PipelineStepContext, usePaymentMethodStore } from '..';
import { useTipStore } from '../../state/tip-store';
import { EncryptionService } from '../services/encryption-service';

import useBuildPaymentProcessor from './use-build-payment-processor';

const DEFAULT_MAX_CART_LIMIT = 0;
const DEFAULT_MIN_DELIVERY_AMOUNT = 0;

export function useBuildPipelineContext() {
  const paymentMethod = usePaymentMethodStore(s => s.selectedPaymentMethod);
  const newCardData = usePaymentMethodStore(s => s.newCardData);
  const savedCardData = usePaymentMethodStore(s => s.savedCardData);
  const creditType = usePaymentMethodStore(s => s.creditType);
  const setEncryptionService = usePaymentMethodStore(s => s.setEncryptionService);
  const { serverOrder } = useCartContext();
  const { user } = useAuthContext();
  const {
    orderPhoneNumber,
    deliveryAddress,
    deliveryInstructions,
    fireOrderIn: orderFireOrderIn,
  } = useOrderContext();
  const { store } = useStoreContext();
  const processor = useBuildPaymentProcessor();

  const tipCents = useTipStore(s => s.totalTipCentsForCommit);
  const delivery: ICommitDeliveryInput = useMemo(() => {
    return {
      instructions: deliveryInstructions,
      tipCents,
      pushNotification: true,
      dropoff: {
        ...deliveryAddress,
        phoneNumber: orderPhoneNumber,
      },
    };
  }, [deliveryAddress, deliveryInstructions, orderPhoneNumber, tipCents]);

  const maxCartLimit: number =
    Number(useFlag(LaunchDarklyFlag.OVERRIDE_CHECKOUT_LIMIT)) || DEFAULT_MAX_CART_LIMIT;
  const minDeliveryAmount: number =
    Number(useFlag(LaunchDarklyFlag.OVERRIDE_CHECKOUT_DELIVERY_MINIMUM)) ||
    DEFAULT_MIN_DELIVERY_AMOUNT;

  const buildPipelineContext = useCallback((): PipelineStepContext | null => {
    const rbiOrderId = serverOrder?.rbiOrderId;
    const storeId = serverOrder?.cart?.storeDetails?.storeNumber;
    const serviceMode = serverOrder?.cart?.serviceMode;
    // typed as IBackendCartEntries
    const cartEntries = serverOrder?.cart?.cartEntries;
    const fireOrderIn = orderFireOrderIn ?? 0;
    const fullName = user?.details?.name;
    // NOTE: This is an unused field on the commitOrder call, setting to false so it doesn't break
    const skipCoolingPeriod = false;
    const cartSubTotal = serverOrder?.cart?.subTotalCents ?? 0;
    const cartTotal = serverOrder?.cart?.totalCents ?? 0;

    const serverOrderStatus = serverOrder?.status ?? '';
    const hasUnavailableCartEntries = (serverOrder?.cart?.unavailableCartEntries?.length ?? 0) > 0;

    // Early return if required fields are missing
    if (!rbiOrderId || !storeId || !serviceMode || !cartEntries || !fullName) {
      return null;
    }

    const baseContext: BasePipelineContext = {
      // type error for COMET and others
      processor: processor as any,
      creditType: creditType as CartPaymentCardType,
      rbiOrderId,
      storeId,
      serviceMode,
      cartEntries,

      fireOrderIn,
      delivery,
      fullName,
      skipCoolingPeriod,

      cartTotal,
      cartSubTotal,
      serverOrderStatus,
      unavailableCartEntries: hasUnavailableCartEntries,
      maxCartLimit,
      minDeliveryAmount,

      // Pass restaurant data from cached store context
      restaurantTimezone: store?.timezone ?? undefined,
      restaurantHours: store
        ? {
            deliveryHours: store.deliveryHours,
            curbsideHours: store.curbsideHours,
            driveThruHours: store.driveThruHours,
            diningRoomHours: store.diningRoomHours,
            pickupWindowHours: store.pickupWindowHours,
            cateringHours: null, // Not available in IStore type
          }
        : undefined,

      errors: [],
    };

    switch (paymentMethod) {
      case 'savedCard': {
        // Saved card flow requires the account identifier
        if (!savedCardData) {
          return null;
        }

        // Both Orbital and Worldpay use accountIdentifier
        const savedCardId = savedCardData.accountIdentifier;

        if (!savedCardId) {
          return null;
        }

        return {
          ...baseContext,
          paymentMethod: 'savedCard',
          savedCardId,
          last4: savedCardData.alias,
          expiryMonth: savedCardData.expiryMonth,
          expiryYear: savedCardData.expiryYear,
          // bin may not be available in savedCardData
        };
      }
      case 'applePay':
      case 'googlePay':
      case 'giftCard': {
        // Digital wallets and gift cards ??  don't need details upfront
        // The pipeline steps will handle requesting/validating them
        return {
          ...baseContext,
          paymentMethod,
        } as PipelineStepContext;
      }
      // new card flow when the paymentMethod is null
      default: {
        // Return null if card data is missing
        if (!newCardData) {
          return null;
        }

        // Create & stash the encryption service for commitOrderStep
        const encryptionService = new EncryptionService();
        setEncryptionService(encryptionService);

        return {
          ...baseContext,
          paymentMethod: 'newCard',
          cardData: newCardData,
        };
      }
    }
  }, [
    serverOrder?.rbiOrderId,
    serverOrder?.cart?.storeDetails?.storeNumber,
    serverOrder?.cart?.serviceMode,
    serverOrder?.cart?.cartEntries,
    serverOrder?.cart?.subTotalCents,
    serverOrder?.cart?.totalCents,
    serverOrder?.cart?.unavailableCartEntries?.length,
    serverOrder?.status,
    user?.details?.name,
    newCardData,
    savedCardData,
    creditType,
    delivery,
    maxCartLimit,
    minDeliveryAmount,
    paymentMethod,
    setEncryptionService,
    store,
    processor,
    orderFireOrderIn,
  ]);

  return { buildPipelineContext };
}
